import { useState, useEffect, useCallback } from 'react';
import { AppUser, UserStatus } from '@/types';
import { useConnection } from '@/context/ConnectionContext';
import { fetchAdminUsersApi, updateUserStatusApi, saveUserDataApi } from '@/services/adminUserApiClient';

export interface UpdateOpStatus {
  loading: boolean;
  error: string | null;
  success: boolean;
}

export const useAdminUsers = (appUser: AppUser | null) => {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [adminUsersMessage, setAdminUsersMessage] = useState<string | null>(null);
  const [updateOpStatus, setUpdateOpStatus] = useState<{ [userId: string]: UpdateOpStatus }>({});
  const { setIsOffline } = useConnection();

  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const fetched = await fetchAdminUsersApi();
      setUsers(fetched);
      setIsLoadingUsers(false);
      setAdminUsersMessage(null);
      setIsOffline(false);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setAdminUsersMessage(error?.message || 'Error al cargar la lista de usuarios');
      setIsLoadingUsers(false);
    }
  }, [setIsOffline]);

  useEffect(() => {
    if (appUser?.role === 'SUPERADMIN') {
      fetchUsers();
    } else {
      setIsLoadingUsers(false);
      setUsers([]);
    }
  }, [fetchUsers, appUser?.role]);

  const updateUserStatus = useCallback(async (userId: string, currentStatus: UserStatus) => {
    setUpdateOpStatus(prev => ({ ...prev, [userId]: { loading: true, error: null, success: false } }));
    try {
      const newStatus = currentStatus === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;
      await updateUserStatusApi(userId, newStatus);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      setUpdateOpStatus(prev => ({ ...prev, [userId]: { loading: false, error: null, success: true } }));
      setTimeout(() => {
        setUpdateOpStatus(prev => ({ ...prev, [userId]: { ...prev[userId], success: false } }));
      }, 3000);
    } catch (error: any) {
      console.error(`Error updating status for user ${userId}:`, error);
      setUpdateOpStatus(prev => ({ ...prev, [userId]: { loading: false, error: error?.message || 'Error al actualizar estado', success: false } }));
    }
  }, []);

  const saveUser = useCallback(async (userData: Partial<AppUser>, userId: string) => {
    try {
      await saveUserDataApi(userId, userData);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...userData } : u));
    } catch (error: any) {
      console.error(`Error saving user ${userId}:`, error);
      const userMessage = error?.message || 'Error al guardar los datos del usuario';
      setAdminUsersMessage(userMessage);
      throw new Error(userMessage);
    }
  }, []);

  return {
    users,
    isLoadingUsers,
    adminUsersMessage,
    setAdminUsersMessage,
    updateOpStatus,
    updateUserStatus,
    saveUser,
    refreshUsers: fetchUsers,
  };
};
